import type { GetServerSidePropsContext } from "next";
import type { BasePageProps, MenuResponse } from "@/types";
import { Box, Stack, Typography, alpha, styled } from "@mui/material";
import { useEffect } from "react";

import PageShell from "@/components/common/PageShell";
import SafeImage from "@/components/common/SafeImage";
import { NO_DISPLAY_IMAGE } from "@/constants/images";
import { fetchMenu } from "@/lib/api";
import { loadBasePageProps } from "@/lib/pageProps";
import { useAppDispatch, useAppSelector } from "@/store";
import { setSettings } from "@/store/slices/settingsSlice";
import { formatCurrency } from "@/utils/format";

const Grid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: theme.spacing(2),
}));

const Tile = styled(Box)(({ theme }) => ({
  borderRadius: 14,
  overflow: "hidden",
  border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
  backgroundColor: theme.palette.background.paper,
  "& img": {
    width: "100%",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    display: "block",
  },
}));

/** Read-only browse view — no basket, used for in-store screens and QR posters. */
interface DigitalMenuPageProps extends BasePageProps {
  menu: MenuResponse | null;
}

// `metadata` / `baseUrl` are consumed by `_app`'s `SeoHead`, not here.
export default function DigitalMenuPage({
  data,
  menu,
}: DigitalMenuPageProps) {
  const dispatch = useAppDispatch();
  const { currency_symbol: symbol, currency_code: code } = useAppSelector(
    (state) => state.settings.data,
  );

  useEffect(() => {
    if (data?.settings) dispatch(setSettings(data.settings));
  }, [data, dispatch]);

  const categories = menu?.categories ?? [];
  const items = menu?.items ?? [];

  return (
    <>
      <PageShell title="Digital menu" maxWidth="md" hideBack>
        {categories.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No categories are published yet.
          </Typography>
        )}
        <Stack spacing={4}>
          {categories.map((category) => (
            <Box key={category._id}>
              <Typography variant="h6" gutterBottom>
                {category.name}
              </Typography>
              <Grid>
                {items
                  .filter((item) => item.category_id === category._id)
                  .map((item) => (
                    <Tile key={item._id}>
                      <SafeImage
                        src={item.image_url || NO_DISPLAY_IMAGE}
                        alt={item.name}
                        loading="lazy"
                      />
                      <Box p={1.5}>
                        <Typography variant="subtitle2" noWrap>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary.main"
                          fontWeight={700}
                        >
                          {formatCurrency(item.price, { symbol, code })}
                        </Typography>
                      </Box>
                    </Tile>
                  ))}
              </Grid>
            </Box>
          ))}
        </Stack>
      </PageShell>
    </>
  );
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<{
  props: DigitalMenuPageProps;
}> {
  // The BRANCH is resolved first (see lib/pageProps.ts); the menu then follows
  // the settings that came back, exactly as it did before.
  const base = await loadBasePageProps(ctx);
  const menu = await fetchMenu({ tenantId: base.data?.settings?._id });
  return { props: { ...base, menu } };
}
